import { Alert, Button } from "antd";
import type { File as EntityFile, Project, PurchaseGoodsReceiptVo, PurchaseReconciliation } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { INVOICE_STATE, RECONCILIATION_STATE, RECONCILIATION_TYPE } from "@/meta/purchase/reconciliation";
import { dateFormat, dateTimeFormat, getRealyState } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { flatPurchaseGoodsReceipts, type PurGoodsReceiptVoRow } from "@/utils/purchaseReconciliation";
import { getShipName } from "@/utils/ship";
import { formatNumber } from "@/utils/number";

export interface PurchaseReconciliationDetailData {
    project?: Partial<Project>;
    purchaseReconciliation?: Partial<PurchaseReconciliation>;
    purchaseGoodsReceiptVos?: PurchaseGoodsReceiptVo[];
    file?: Partial<EntityFile>;
}

export interface PurchaseReconciliationDetailPageProps {
    detailId?: number | string;
    loadDetail?: (id: number | string) => Promise<PurchaseReconciliationDetailData>;
    loadFile?: (id: number | string) => Promise<Partial<EntityFile>>;
}

/** 直接复用 reconciliationService.findById 的 Vue API 路径，避免把 decorator 带入 React 页面。 */
export async function loadPurchaseReconciliationDetail(id: number | string): Promise<PurchaseReconciliationDetailData> {
    const response = await request.service.get<PurchaseReconciliationDetailData>(
        `/admin/purchase/reconciliation/vo/${id}`
    );
    return response.data;
}

/** 复用 Vue BaseItem 的 fileId 契约，按文件编号获取对账文件资源。 */
export async function loadPurchaseReconciliationFile(id: number | string): Promise<Partial<EntityFile>> {
    const response = await request.service.get<Partial<EntityFile>>(`/dataService/admin/file/${id}`);
    return response.data;
}

/** Vue BaseItem 使用该占位符表示未返回的详情字段。 */
function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);
}

/** 详情日期保持 Vue 页面对应的日期精度。 */
function formatDetailDate(value?: string): string {
    return value ? dateFormat(value, DATE_PICKER_FORMAT) : DEFAULT_VALUE;
}

/** 详情头部创建时间保持 Vue DATETIME_PICKER_FORMAT 的分钟精度。 */
function formatCreatedTime(value?: string): string {
    return value ? dateFormat(value, DATETIME_PICKER_FORMAT) : DEFAULT_VALUE;
}

/** 详情作废时间保持 Vue dateTimeFormat 的分钟精度。 */
function formatInvalidTime(value?: string): string {
    return value ? dateTimeFormat(value) : DEFAULT_VALUE;
}

/** 总吨数保留接口返回的小数位，并将数值与单位拼成一个可查询文本。 */
function formatQuantity(value?: number): string {
    if (typeof value !== "number") return `${DEFAULT_VALUE}吨`;
    return `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}吨`;
}

/** 读取 Vue 详情页使用的高位真实状态，不混入列表页的低位兼容逻辑。 */
function realState(value?: number | string): number {
    return getRealyState(Number(value));
}

/** 使用 Vue 枚举文案展示状态，未知或缺失状态显示默认占位符。 */
function stateLabel(value?: number | string): string {
    return RECONCILIATION_STATE.getIntroByCode(realState(value)) || DEFAULT_VALUE;
}

/** 只在已完成状态下展示 Vue 详情页的开票状态。 */
function invoiceStateLabel(reconciliation?: Partial<PurchaseReconciliation>): string {
    if (realState(reconciliation?.state) !== RECONCILIATION_STATE.COMPLETED.code) return DEFAULT_VALUE;
    return reconciliation?.invoiceState
        ? INVOICE_STATE.getIntroByCode(reconciliation.invoiceState) || DEFAULT_VALUE
        : DEFAULT_VALUE;
}

/** 计算 Vue mergeSpan 的连续相同值行数，供 zhihao Table spanMethod 使用。 */
function rowSpanFor(
    rows: PurGoodsReceiptVoRow[],
    rowIndex: number,
    getValue: (row: PurGoodsReceiptVoRow) => unknown
): number {
    const currentValue = getValue(rows[rowIndex]);
    if (rowIndex > 0 && getValue(rows[rowIndex - 1]) === currentValue) return 0;

    let rowSpan = 1;
    for (let index = rowIndex + 1; index < rows.length; index += 1) {
        if (getValue(rows[index]) !== currentValue) break;
        rowSpan += 1;
    }
    return rowSpan;
}

type ReceiptTableRow = PurGoodsReceiptVoRow & { tableKey: string };

/** 生成与 Vue ProTable 一致的签收单字段和格式，行合并由 zhihao spanMethod 统一处理。 */
function createReceiptColumns(): TableColumnProps<ReceiptTableRow>[] {
    const receiptId = (row: PurGoodsReceiptVoRow) => row.purchaseGoodsReceipt?.id;
    const receiptDate = (row: PurGoodsReceiptVoRow) => row.purchaseGoodsReceipt?.receiptConfirmedDate;

    return [
        {
            key: "id",
            label: "签收编号",
            prop: "id",
            render: ({ row }) => {
                const id = receiptId(row);
                return id == null ? (
                    DEFAULT_VALUE
                ) : (
                    <Link to={`/purchasingManage/receiptManagement/detail/${id}`}>{id}</Link>
                );
            }
        },
        {
            key: "receiptConfirmedDate",
            label: "确认日期",
            prop: "receiptConfirmedDate",
            render: ({ row }) => formatDetailDate(receiptDate(row))
        },
        {
            key: "shipName",
            label: "船名",
            prop: "shipName",
            render: ({ row }) => getShipName(row.purchaseGoods?.ship || {})
        },
        {
            key: "receiptQuantity",
            label: "签收吨数",
            prop: "receiptQuantity",
            render: ({ row }) => formatNumber(row.purchaseGoods?.ship?.receiptQuantity)
        },
        {
            key: "receiptDate",
            label: "签收日期",
            prop: "receiptDate",
            render: ({ row }) => formatDetailDate(row.purchaseGoods?.ship?.receiptDate)
        },
        {
            key: "unitPrice",
            label: "单价(元/吨)",
            prop: "unitPrice",
            render: ({ row }) => formatNumber(row.purchaseGoods?.ship?.unitPrice, 2)
        },
        {
            key: "subtotal",
            label: "小计(元)",
            prop: "subtotal",
            render: ({ row }) => {
                const quantity = row.purchaseGoods?.ship?.receiptQuantity;
                const unitPrice = row.purchaseGoods?.ship?.unitPrice;
                return formatNumber(
                    typeof quantity === "number" && typeof unitPrice === "number" ? quantity * unitPrice : undefined,
                    2
                );
            }
        }
    ];
}

/** 采购对账详情：迁移 Vue 字段、签收单、跳转、返回和加载失败反馈。 */
export function PurchaseReconciliationDetailPage({
    detailId,
    loadDetail = loadPurchaseReconciliationDetail,
    loadFile = loadPurchaseReconciliationFile
}: PurchaseReconciliationDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = detailId ?? params.id ?? "";
    const [detail, setDetail] = useState<PurchaseReconciliationDetailData>();
    const [reconciliationFile, setReconciliationFile] = useState<Partial<EntityFile>>();
    const [invalidFile, setInvalidFile] = useState<Partial<EntityFile>>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadDetail(currentId)
            .then(value => {
                if (disposed) return;
                setDetail(value);
                // Vue BaseItem 先展示详情响应中的文件，再按 fileId 查询最新文件资源。
                setReconciliationFile(value.file);
                setInvalidFile(undefined);
                setErrorMessage(null);

                const reconciliationFileId = value.purchaseReconciliation?.fileId ?? value.file?.id;
                if (reconciliationFileId !== undefined && reconciliationFileId !== null) {
                    void loadFile(reconciliationFileId)
                        .then(file => {
                            if (!disposed) setReconciliationFile(file);
                        })
                        .catch(() => {
                            // 文件服务失败时保留详情响应或 fileId，避免阻断对账详情展示。
                        });
                }

                const invalidFileId = value.purchaseReconciliation?.invalidFileId;
                if (invalidFileId !== undefined && invalidFileId !== null) {
                    void loadFile(invalidFileId)
                        .then(file => {
                            if (!disposed) setInvalidFile(file);
                        })
                        .catch(() => {
                            // 作废文件服务失败时保留原 fileId，确保作废信息仍可识别。
                        });
                }
            })
            .catch(() => {
                if (disposed) return;
                setDetail(undefined);
                setErrorMessage("采购对账详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadDetail, loadFile]);

    const reconciliation = detail?.purchaseReconciliation;
    const currentRealState = realState(reconciliation?.state);
    const receiptRows = useMemo<ReceiptTableRow[]>(
        () =>
            flatPurchaseGoodsReceipts(detail?.purchaseGoodsReceiptVos || []).map((row, index) => ({
                ...row,
                tableKey: `${row.purchaseGoodsReceipt?.id ?? DEFAULT_VALUE}-${row.purchaseGoods?.id ?? DEFAULT_VALUE}-${index}`
            })),
        [detail?.purchaseGoodsReceiptVos]
    );
    const receiptColumns = useMemo(() => createReceiptColumns(), []);
    const projectName = display(reconciliation?.projectName || detail?.project?.name);
    const projectId = reconciliation?.projectId ?? detail?.project?.id;
    const invoiceId = reconciliation?.invoiceId;

    /** 按 Vue BaseItem 的 fileId 行为展示文件名，并在资源可用时提供查看/下载入口。 */
    const renderFileValue = (file: Partial<EntityFile> | undefined, fileId?: number | string) =>
        file?.id !== undefined && file?.id !== null && file.path ? (
            <span className="inline-flex items-center gap-4">
                <span>{display(file.name || file.id)}</span>
                <a href={file.path} rel="noreferrer" target="_blank">
                    查看/下载
                </a>
            </span>
        ) : (
            display(file?.name || fileId)
        );

    return (
        <section aria-label="purchase-reconciliation-detail-page" className="flex min-h-full flex-col gap-4">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: display(reconciliation?.id) }]}
                    rightInfos={[{ label: "创建时间", value: formatCreatedTime(reconciliation?.createdTime) }]}
                    stateDesc={stateLabel(reconciliation?.state)}
                    title="对账详情"
                />
                {errorMessage ? <Alert className="mb-4" role="alert" title={errorMessage} type="error" /> : null}
            </div>

            <div className="section-card">
                <div className="section-title">基本信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                    <ZhBaseItem
                        label="所属项目"
                        value={
                            projectId == null ? (
                                projectName
                            ) : (
                                <Link to={`/purchasingManage/project/info/${projectId}`}>{projectName}</Link>
                            )
                        }
                    />
                    <ZhBaseItem
                        label="类型"
                        value={RECONCILIATION_TYPE.getIntroByCode(reconciliation?.type) || DEFAULT_VALUE}
                    />
                    <ZhBaseItem label="品类" value={display(reconciliation?.productTypeName)} />
                    <div aria-hidden="true" />
                    <ZhBaseItem label="供货单位" value={getEnterpriseName(reconciliation?.supplierEnterprise)} />
                    <ZhBaseItem label="对账单位" value={getEnterpriseName(reconciliation?.purchaseEnterprise)} />
                    <ZhBaseItem label="总吨数" value={formatQuantity(reconciliation?.quantity)} />
                    <ZhBaseItem label="日期" value={formatDetailDate(reconciliation?.reconciliationDate)} />
                    <ZhBaseItem label="核算金额" value={`${formatNumber(reconciliation?.calculatedAmount, 2)}元`} />
                    <ZhBaseItem label="折扣金额" value={`${formatNumber(reconciliation?.actualAmount, 2)}元`} />
                    <ZhBaseItem label="是否已开票" value={invoiceStateLabel(reconciliation)} />
                    <ZhBaseItem
                        label="关联开票"
                        value={
                            invoiceId ? (
                                <Link to={`/purchasingManage/invoiceManagement/detail/${invoiceId}`}>{invoiceId}</Link>
                            ) : (
                                DEFAULT_VALUE
                            )
                        }
                    />
                    <ZhBaseItem label="备注" span={4} value={display(reconciliation?.remark)} />
                    <ZhBaseItem label="对账文件" span={4}>
                        {renderFileValue(reconciliationFile, reconciliation?.fileId ?? detail?.file?.id)}
                    </ZhBaseItem>
                </ZhGrid>
            </div>

            {currentRealState === RECONCILIATION_STATE.INVALID.code ||
            currentRealState === RECONCILIATION_STATE.INVALIDING.code ? (
                <div className="section-card">
                    <div className="section-title">作废信息</div>
                    <ZhGrid className="mt-5" cols={4} gap={[20, 30]}>
                        <ZhBaseItem label="文件">
                            {renderFileValue(invalidFile, reconciliation?.invalidFileId)}
                        </ZhBaseItem>
                        {currentRealState === RECONCILIATION_STATE.INVALID.code ? (
                            <ZhBaseItem label="时间" value={formatInvalidTime(reconciliation?.invalidTime)} />
                        ) : null}
                    </ZhGrid>
                </div>
            ) : null}

            <div className="section-card">
                <div className="section-title">签收单</div>
                <ZhTable<ReceiptTableRow>
                    data={receiptRows}
                    columns={receiptColumns}
                    emptyText="暂无签收单"
                    pagination={false}
                    rowKey="tableKey"
                    // 使用 zhihao 的 spanMethod 保留 Vue mergeSpan 对签收单和货物的行合并。
                    spanMethod={({ column, rowIndex }) => {
                        const receiptId = (row: PurGoodsReceiptVoRow) => row.purchaseGoodsReceipt?.id;
                        const receiptDate = (row: PurGoodsReceiptVoRow) =>
                            row.purchaseGoodsReceipt?.receiptConfirmedDate;
                        const goodsId = (row: PurGoodsReceiptVoRow) => row.purchaseGoods?.id;
                        const property =
                            column.prop === "id" || column.prop === "receiptConfirmedDate"
                                ? column.prop
                                : ["shipName", "receiptQuantity", "receiptDate", "unitPrice", "subtotal"].includes(
                                        column.prop || ""
                                    )
                                  ? "goodsId"
                                  : undefined;
                        if (property === "id") return { rowspan: rowSpanFor(receiptRows, rowIndex, receiptId) };
                        if (property === "receiptConfirmedDate") {
                            return { rowspan: rowSpanFor(receiptRows, rowIndex, receiptDate) };
                        }
                        return property === "goodsId"
                            ? { rowspan: rowSpanFor(receiptRows, rowIndex, goodsId) }
                            : undefined;
                    }}
                />
            </div>
            <Button onClick={() => void navigate("/purchasingManage/purchaseReconciliation")}>返回</Button>
        </section>
    );
}

export default PurchaseReconciliationDetailPage;
